/**
 * ActionListChat socket API (client helper)
 *
 * Runtime code is plain JS (project has no TS build). Types live in:
 * - src/types/actionListChat.d.ts
 */

/**
 * @typedef {"rename"|"checkPlan"|"create"|"updateUsers"} ActionListChatMethod
 * @typedef {"list"|"chat"} ActionListChatEntity
 * @typedef {"add"|"remove"|"change"|"remove/add"} ActionListChatAction
 *
 * @typedef {Object} ActionListChatRequest
 * @property {ActionListChatMethod} method
 * @property {ActionListChatEntity} entity
 * @property {ActionListChatAction} [action]
 * @property {Record<string, any>} payload
 */

let pendingApprovalRequest = null;
let defaultSocket = null;

function clone(obj) {
  try {
    return JSON.parse(JSON.stringify(obj));
  } catch (e) {
    return obj;
  }
}

function validateRequest(req) {
  if (!req || typeof req !== "object") {
    throw new Error("IN_ActionListChat: request must be an object");
  }
  if (!req.method) throw new Error("IN_ActionListChat: method is required");
  if (!req.entity) throw new Error("IN_ActionListChat: entity is required");
  if (!req.payload) throw new Error("IN_ActionListChat: payload is required");
}

function shouldStoreForApproval(req) {
  return req.method === "updateUsers" && req.payload && !req.payload.approved;
}

/**
 * Send request to backend.
 *
 * @param {import("socket.io-client").Socket} socket
 * @param {ActionListChatRequest} req
 */
function resolveSocket(socket) {
  return socket || defaultSocket;
}

function setSocket(socket) {
  defaultSocket = socket;
}

/**
 * Overloads:
 * - request(req)               // uses default socket (setSocket)
 * - request(socket, req)
 *
 * @param {any} socketOrReq
 * @param {any} maybeReq
 */
function request(socketOrReq, maybeReq) {
  const req = maybeReq ? maybeReq : socketOrReq;
  const socket = resolveSocket(maybeReq ? socketOrReq : null);

  validateRequest(req);

  if (!socket) {
    throw new Error(
      "IN_ActionListChat: socket instance is required (pass it or call setSocket)"
    );
  }

  if (shouldStoreForApproval(req)) {
    pendingApprovalRequest = clone(req);
  }

  socket.emit("IN_ActionListChat", req);
}

function getPendingApprovalRequest() {
  return pendingApprovalRequest ? clone(pendingApprovalRequest) : null;
}

function clearPendingApprovalRequest() {
  pendingApprovalRequest = null;
}

/**
 * Approve and resend last stored updateUsers request.
 *
 * @param {import("socket.io-client").Socket} socket
 * @returns {boolean} true if resent
 */
function approvePendingUpdateUsers(socket) {
  socket = resolveSocket(socket);
  const req = getPendingApprovalRequest();
  if (!req) return false;
  if (req.method !== "updateUsers") return false;

  req.payload = req.payload || {};
  req.payload.approved = true;

  // keep pending in case backend asks again; clear after we successfully resend
  request(socket, req);
  clearPendingApprovalRequest();
  return true;
}

export default {
  setSocket,
  request,
  getPendingApprovalRequest,
  clearPendingApprovalRequest,
  approvePendingUpdateUsers,
};
